import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type CreateDistrict, DistrictSchemas } from "@workspace/contracts/districts";
import { LanguageValues } from "@workspace/contracts/language";
import { Separator } from "@workspace/ui/components/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/ui/components/tabs";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { useAppForm } from "@/components/form-inputs";
import { AutoFillAltText } from "@/components/form-inputs/auto-fill-alt-text";
import { TranslationGroupField } from "@/components/form-inputs/field/translation-group";
import { SlugField } from "@/features/common/slug-field";
import { DistrictSectionGroupField } from "@/features/district/district-sections-group";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/districts/new")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const citiesList = useQuery(orpc.cities.options.queryOptions({ input: {} }));

  const { mutateAsync } = useMutation(orpc.districts.create.mutationOptions());

  const form = useAppForm({
    defaultValues: {
      cityId: "",
      slug: undefined,
      image: undefined,
      translations: {
        en: { title: "", description: "" },
        ar: { title: "", description: "" },
        tr: { title: "", description: "" },
        fa: { title: "", description: "" },
      },
      sections: [],
      altText: "",
    } as CreateDistrict,
    validators: {
      onSubmit: DistrictSchemas.create,
    },
    onSubmit: async ({ value }) => {
      try {
        await mutateAsync(value);
        toast.success("District has been created successfully");

        queryClient.invalidateQueries({ queryKey: orpc.districts.list.queryKey({ input: {} }) });

        navigate({ to: "/districts" });
      } catch (error) {
        const msg = getError(error, "Failed to create district");
        toast.error(msg);
      }
    },
  });

  const onSubmitHandler = (e: React.SubmitEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <Main>
      <form onSubmit={onSubmitHandler}>
        <Tabs defaultValue="district" className="relative w-full">
          <TabsList>
            <TabsTrigger value="district">District</TabsTrigger>
            <TabsTrigger value="sections">Sections</TabsTrigger>
          </TabsList>

          <TabsContent value="district" className="grid grid-cols-2 gap-4 pt-5">
            <form.Subscribe selector={(state) => ({ title: state.values.translations.en.title, altText: state.values.altText ?? "" })}>
              {({ title, altText }) => <AutoFillAltText title={title} altText={altText} setAltText={(value) => form.setFieldValue("altText", value)} />}
            </form.Subscribe>

            <TranslationGroupField
              label="Name"
              type="text"
              form={form}
              fields={{
                en: "translations.en.title",
                ar: "translations.ar.title",
                tr: "translations.tr.title",
                fa: "translations.fa.title",
              }}
            />

            <Separator className="col-span-2 my-4" />

            <Tabs defaultValue="en" className="col-span-2">
              <TabsList>
                <TabsTrigger value="en">English</TabsTrigger>
                <TabsTrigger value="ar">Arabic</TabsTrigger>
                <TabsTrigger value="tr">Turkish</TabsTrigger>
                <TabsTrigger value="fa">Farsi</TabsTrigger>
              </TabsList>

              {LanguageValues.map((lang) => (
                <TabsContent key={lang} value={lang}>
                  <form.AppField name={`translations.${lang}.description`}>
                    {(field) => (
                      <field.EditorField
                        direction={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                        preview
                        label={`${lang.toUpperCase()} Description`}
                      />
                    )}
                  </form.AppField>
                </TabsContent>
              ))}
            </Tabs>

            <Separator className="col-span-2 my-4" />

            <form.Subscribe selector={(state) => state.values.translations.en.title}>
              {(name) => (
                <form.AppField name="slug">
                  {(field) => (
                    <SlugField
                      name={name}
                      onBlur={field.handleBlur}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.AppField>
              )}
            </form.Subscribe>

            <form.AppField name="cityId">
              {(field) => (
                <field.SelectField options={citiesList.data || []} label="City" placeholder="Select district" />
              )}
            </form.AppField>

            <Separator className="col-span-2 my-4" />

            <form.AppField name="image">
              {(field) => <field.ImageField label="Image" classNames={{ wrapper: "col-span-2" }} />}
            </form.AppField>

            <form.Subscribe selector={(state) => state.values.image}>
              {(image) => (
                <form.AppField name="altText">
                  {(field) => <field.TextField disabled={!image} label="Alt Text" type="text" />}
                </form.AppField>
              )}
            </form.Subscribe>
          </TabsContent>

          <TabsContent value="sections">
            <DistrictSectionGroupField form={form} fields={{ sections: "sections" }} />
          </TabsContent>
        </Tabs>

        <form.AppForm>
          <form.SubmitButton className="float-end mt-8">Submit</form.SubmitButton>
        </form.AppForm>
      </form>
    </Main>
  );
}
